"use client";

import {
  IDedicatedResourceRef,
  IUserFull,
  WorkplaceRequestElement,
  WorkplaceRequestReleasedResourceAction,
  WorkplaceRequestTypeElement,
  WorkplaceRequestWorkflowRun
} from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import { createMapLink } from "@gettactic/helpers/src/navigation/createMapLink";
import { useResourcesByIds, useUserList } from "@gettactic/hooks";
import {
  Armchair,
  CalendarClock,
  CircleCheck,
  CircleX,
  MapPin
} from "lucide-react";
import Link from "next/link";
import React, { useMemo } from "react";
import {
  useDeskAutomationSteps,
  useVacatedDedicatedDesk
} from "../hooks/useDeskAutomation";
import {
  type DeskAutomationStep,
  deskAutomationMoveNote,
  deskAutomationSentence,
  hasReportedOutcome,
  type ReleasedDeskOutcome,
  releasedDeskFateNote,
  releasedDeskOutcomeSentence,
  releasedDeskOutcomes,
  type VacatedDesk
} from "../lib/desk-automation";
import { formatRequestDate } from "../lib/formatRequestDate";
import {
  describeWorkflowRunError,
  type WorkflowRunErrorContext
} from "../lib/workflowRunError";

interface DeskAutomationPanelProps {
  request: WorkplaceRequestElement;
  /** The request's type, carrying the workflows to describe. */
  type: WorkplaceRequestTypeElement | undefined;
  /**
   * The approver's picks, by workflow id, while the approval box is open. What
   * the panel promises has to follow what the approver just chose, not the
   * rule they chose to disagree with.
   */
  releasedDeskOverrides?: Record<
    string,
    WorkplaceRequestReleasedResourceAction
  >;
  className?: string;
}

const RUN_TONE: Record<
  WorkplaceRequestWorkflowRun["status"],
  { label: string; className: string }
> = {
  success: { label: "Done", className: "bg-green-50 text-green-700" },
  scheduled: { label: "Scheduled", className: "bg-indigo-50 text-indigo-700" },
  pending: { label: "Running", className: "bg-neutral-100 text-neutral-600" },
  skipped: { label: "Skipped", className: "bg-amber-50 text-amber-700" },
  failed: { label: "Failed", className: "bg-red-50 text-red-700" }
};

/**
 * Spells out, in plain language, what approving this request will do to the
 * map. Once it has been approved the same rows carry the outcome of each run,
 * so the panel answers "what will happen" and "what did happen" in one place.
 *
 * Runs come back only for people who manage the request's type; for everyone
 * else the panel stays a description of the configured rules.
 */
export const DeskAutomationPanel: React.FC<DeskAutomationPanelProps> = ({
  request,
  type,
  releasedDeskOverrides,
  className
}) => {
  const { steps, runByWorkflowId } = useDeskAutomationSteps(request, type);

  // A run's error names the field it couldn't read by id; only the type knows
  // which question that was.
  const errorContext = useMemo(
    () => ({
      fieldNameById: new Map(
        (type?.fields ?? []).map((field) => [field.id, field.name])
      )
    }),
    [type?.fields]
  );

  const usersQuery = useUserList();

  const hasRun = runByWorkflowId.size > 0;

  // What each finished run actually did to the desks it freed. This is the
  // record, so it outranks anything the rule was configured with — an approver
  // may have overridden it on the way through.
  const outcomesByWorkflowId = useMemo(
    () =>
      new Map(
        steps.map((step) => [
          step.workflowId,
          releasedDeskOutcomes(runByWorkflowId.get(step.workflowId))
        ])
      ),
    [steps, runByWorkflowId]
  );

  // Before the move there is no record to read, so the desk being vacated is
  // looked up on the person — dedicated moves only, and only when unambiguous.
  const vacatedDesk = useVacatedDedicatedDesk(steps, runByWorkflowId);

  // Every desk that needs a name: the one each rule targets, plus the ones the
  // runs freed — those come back as bare ids.
  const resourceIds = useMemo(
    () =>
      Array.from(
        new Set([
          ...steps
            .map((step) => step.resourceId)
            .filter((id): id is string => id !== null),
          ...Array.from(outcomesByWorkflowId.values())
            .flat()
            .map((outcome) => outcome.resourceId)
        ])
      ),
    [steps, outcomesByWorkflowId]
  );
  const { resources } = useResourcesByIds(resourceIds);

  const userName = (id: string | null): string | null => {
    if (!id) return null;
    const user = (usersQuery.data?.elements as IUserFull[] | undefined)?.find(
      (u) => u.id === id
    );
    return user?.name || user?.email || null;
  };

  const resourceName = (id: string): string | null =>
    resources.find((r) => r.id === id)?.name ?? null;

  // Nothing to say when the type carries no desk rules. And when it doesn't
  // require approval its rules never fire — core publishes the trigger only on
  // responding to a pending approval — so describing what approval would do
  // would be describing something that can't happen. Runs that did happen are
  // still shown: those are a record, not a promise.
  if (steps.length === 0) return null;
  if (!type?.requires_approval && !hasRun) return null;

  return (
    <section
      className={cn(
        "rounded-xl border border-indigo-100 bg-indigo-50/50 p-4",
        className
      )}
    >
      <h3 className="flex items-center gap-2 font-semibold text-indigo-900 text-sm">
        <Armchair className="h-4 w-4 shrink-0" />
        {hasRun ? "Desk automation" : "What happens on approval"}
      </h3>

      <ul className="mt-3 flex flex-col gap-2.5">
        {steps.map((step) => {
          const resource = step.resourceId
            ? resources.find((r) => r.id === step.resourceId)
            : undefined;
          return (
            <AutomationRow
              key={step.workflowId}
              step={step}
              personName={userName(step.personId)}
              deskName={resource?.name ?? null}
              // The desk being given up can sit on a floor the drawer's map
              // isn't showing, so a release always offers its own way there.
              mapHref={
                step.action === "release" && resource
                  ? createMapLink({
                      date: step.runsOn || new Date().toISOString(),
                      officeId: resource.office_id,
                      resourceId: resource.id,
                      section: "desks"
                    })
                  : null
              }
              run={runByWorkflowId.get(step.workflowId)}
              releasedOutcomes={outcomesByWorkflowId.get(step.workflowId) ?? []}
              releasedDeskName={resourceName}
              releasedDeskAction={
                releasedDeskOverrides?.[step.workflowId] ??
                step.releasedResourceAction
              }
              vacatedDesk={vacatedDesk(step)}
              errorContext={errorContext}
            />
          );
        })}
      </ul>
    </section>
  );
};

interface AutomationRowProps {
  step: DeskAutomationStep;
  personName: string | null;
  deskName: string | null;
  /** Set on a release, whose desk may be on another floor. */
  mapHref: string | null;
  run: WorkplaceRequestWorkflowRun | undefined;
  /** What the run did to the desks it freed; empty until it has run. */
  releasedOutcomes: ReleasedDeskOutcome[];
  /** Names one of those freed desks, which arrive as bare ids. */
  releasedDeskName: (resourceId: string) => string | null;
  /** What is to become of the freed desk: the approver's pick, else the rule's. */
  releasedDeskAction: WorkplaceRequestReleasedResourceAction;
  /** The desk they hold today, as far as it could be looked up. */
  vacatedDesk: VacatedDesk<IDedicatedResourceRef>;
  errorContext: WorkflowRunErrorContext;
}

const AutomationRow: React.FC<AutomationRowProps> = ({
  step,
  personName,
  deskName,
  mapHref,
  run,
  releasedOutcomes,
  releasedDeskName,
  releasedDeskAction,
  vacatedDesk,
  errorContext
}) => {
  const sentence = deskAutomationSentence(step, { personName, deskName });
  // Once the run has said what it did, that replaces every prediction about
  // the freed desk — including one an approver overrode on the way through,
  // and including a run that freed nothing at all.
  const hasReported = hasReportedOutcome(run);
  const moveNote = hasReported
    ? null
    : deskAutomationMoveNote(
        step,
        vacatedDesk.kind === "known"
          ? { kind: "known", desk: vacatedDesk.desk.name }
          : vacatedDesk
      );
  // A move that frees nothing has no desk for the action to apply to.
  const fateNote =
    hasReported || vacatedDesk.kind === "none"
      ? null
      : releasedDeskFateNote(step, releasedDeskAction);
  const tone = run ? RUN_TONE[run.status] : null;
  const Icon = step.runsOn ? CalendarClock : CircleCheck;

  return (
    <li className="flex items-start gap-2 text-sm">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-indigo-500" />
      <div className="min-w-0 flex-1">
        <p className="text-indigo-950">{sentence}</p>
        <p className="mt-0.5 text-indigo-700/80 text-xs">
          {step.runsOn
            ? `Scheduled for ${formatRequestDate(step.runsOn)}. Approved after that date, it runs right away.`
            : "Runs as soon as the request is approved."}
        </p>
        {moveNote ? (
          <p className="mt-0.5 text-indigo-700/80 text-xs">{moveNote}</p>
        ) : null}
        {fateNote ? (
          <p className="mt-0.5 text-indigo-700/80 text-xs">{fateNote}</p>
        ) : null}
        {releasedOutcomes.map((outcome) => (
          <p
            key={outcome.resourceId}
            className="mt-0.5 text-indigo-700/80 text-xs"
          >
            {releasedDeskOutcomeSentence(
              outcome,
              releasedDeskName(outcome.resourceId)
            )}
          </p>
        ))}
        {mapHref ? (
          <Link
            href={mapHref}
            className="mt-1 inline-flex items-center gap-1 font-medium text-indigo-700 text-xs hover:underline"
          >
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            View on map
          </Link>
        ) : null}
        {run?.error ? (
          // The raw message stays on the hover: it is what a bug report needs.
          <p
            title={run.error}
            className="mt-1 flex items-start gap-1.5 text-red-700 text-xs"
          >
            <CircleX className="mt-px h-3.5 w-3.5 shrink-0" />
            {describeWorkflowRunError(run.error, errorContext)}
          </p>
        ) : null}
      </div>
      {tone ? (
        <span
          className={cn(
            "shrink-0 rounded-md px-1.5 py-0.5 font-medium text-xs",
            tone.className
          )}
        >
          {tone.label}
        </span>
      ) : null}
    </li>
  );
};
